/**
 * DeliverPrintTickets
 */
import { ReactComponent as DownloadIcon } from 'app/assets/img/download.svg';
import { LoadingContainer } from 'app/components/LoadingContainer';
import { StyledStack } from 'app/components/Stack';
import StyledButton from 'app/components/StyledButton';
import StyledTable from 'app/components/StyledTable';
import useGetApiData from 'app/hooks/useGetApiData';
import useWindowSize from 'app/hooks/useWindowSize';
import {
  selectInfoUser,
  selectTranslations,
} from 'app/pages/App/slice/selectors';
import { I_User } from 'app/pages/App/slice/types';
import {
  downloadAndViewPdf,
  downloadPdfApi,
  getOrderDetailApi,
} from 'app/services/checkDeliverPrintTicketApi';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { notify } from 'reapop';
import checkDeliverPrintTickets from '../utils/checkDeliverPrintTickets';
import DownLoadIcon from './_DownLoadIcon';
import DownLoadModal from './_DownLoadModal';
import { LoadingOutlined } from '@ant-design/icons';
import { sortByKey } from './constant';
import OTPVerification from './OTPVerification';
import { Tooltip } from 'antd';
import moment from 'moment';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';

interface Props {}

// use this const as a key to store data in session storage and local storage
export const DELIVER_PRINT_TICKETS_GUEST_FLOW_DATA =
  'deliverPrintTicketsGuestFlowData';

export const DeliverPrintTickets = memo((props: Props) => {
  const location = useLocation();
  const { id, lang }: { id: string; lang: string } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDownAll, setIsLoadingDownAll] = useState(false);
  const [isLoadingViewAll, setIsLoadingViewAll] = useState(false);
  const translations = useSelector(selectTranslations);
  const [selected, setSelected] = useState<Array<any>>([]);
  const { device } = useWindowSize();
  const isMobile = device !== 'desktop';
  const infoUser: I_User | undefined = useSelector(selectInfoUser);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [isVerifyOtp, setIsVerifyOtp] = useState(false);
  const [data, setData] = useState<Record<string, any> | null>(null);
  const [rawError, setRawError] = useState<Record<string, any> | null>(null);
  const otpRef = useRef('');
  const [otp, setOtp] = useState('');
  const dispatch = useDispatch();
  const { accessDeliverPrintTicketFromEmailLink } = checkDeliverPrintTickets();
  const { locale, onChangeLanguage } = useGlobalFunction();
  const getOrderDetailPayload = useMemo(() => {
    const otpPassed = sessionStorage.getItem(
      DELIVER_PRINT_TICKETS_GUEST_FLOW_DATA,
    );
    sessionStorage.removeItem(DELIVER_PRINT_TICKETS_GUEST_FLOW_DATA);
    if (!!otpPassed) otpRef.current = otpPassed;
    return {
      lang,
      id,
      otp: otpPassed,
    };
  }, [lang, id]);
  useEffect(() => {
    if (!lang) return;
    type Lang = 'vi' | 'en';
    const nextLang: Lang = lang === 'vi' || lang === 'en' ? lang : 'en';
    if (lang && locale !== nextLang) {
      onChangeLanguage(nextLang);
    }
  }, [lang, locale]);
  const {
    data: orderDetailData,
    isLoading: isLoadingDetail,
    rawError: rawErrorDetail,
  } = useGetApiData({
    api: getOrderDetailApi,
    params: getOrderDetailPayload,
  });

  useEffect(() => {
    setData(orderDetailData);
  }, [orderDetailData]);

  useEffect(() => {
    setRawError(rawErrorDetail);
  }, [rawErrorDetail]);

  const onVerifyOtp = async (otp: string) => {
    if (isVerifyOtp) return;
    try {
      setIsVerifyOtp(true);
      const { data } = await getOrderDetailApi({ lang, id, otp });
      setData(data);
      setRawError(null);
      otpRef.current = otp;
      setOtp(otp);
    } catch (error: any) {
      const errMsg = error?.message;
      if (typeof errMsg === 'string') {
        dispatch(notify(errMsg, 'error'));
      }
    } finally {
      setIsVerifyOtp(false);
    }
  };

  const maxFile = data?.maxFile || 10;

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  useEffect(() => {
    const totalTicket = searchParams.get('totalTicket') || '';

    if (accessDeliverPrintTicketFromEmailLink && !otp) return;

    if (Number(totalTicket) === 1) {
      downloadPdfApi(
        id,
        setIsLoading,
        undefined,
        otpRef.current,
        accessDeliverPrintTicketFromEmailLink ? '1' : undefined,
        lang,
      );
    }
  }, [id, searchParams, accessDeliverPrintTicketFromEmailLink, otp, lang]);

  const dataSources = useMemo(() => {
    const listTicketFormat = data?.items?.map((item, index) => {
      return {
        ...item,
        key: item?.ticketCode || item?.verifyCode,
        productName: item?.productName,
        orderItemId: item?.ticketId,
        catalog: item?.parkName,
      };
    });
    const listConcat = [...(listTicketFormat || [])];
    const sortedList = sortByKey(listConcat, 'productName');
    return sortedList.map((item, index) => ({
      ...item,
      index,
      checkBoxDisable:
        !selected.map((res: any) => res.key).includes(item.key) &&
        selected?.length >= maxFile,
    }));
  }, [data?.items, maxFile, selected]);

  const selectedParams = useMemo(() => {
    return {
      orderId: id,
      lang: lang,
      tickets: sortByKey(selected, 'index').map(ticket => ({
        orderItemId: ticket?.orderItemId,
        code: ticket?.ticketCode || ticket?.verifyCode,
        vgsTicketId: ticket?.verifyCode || ticket?.ticketCode,
      })),
    };
  }, [id, selected, lang]);

  const columns = useMemo(
    () => [
      {
        title: (
          <DownLoadModal
            selectedParams={selectedParams}
            submitNumber={id}
            maxFile={maxFile}
            otp={otpRef.current}
            guest={accessDeliverPrintTicketFromEmailLink ? '1' : undefined}
          />
        ),
        dataIndex: 'parkName',
        key: 'parkName',
        render: value => {
          return <div style={{ textAlign: 'left' }}>{value}</div>;
        },
      },
      {
        key: 'productName',
        render: value => {
          const objParams: Record<string, string> = {
            orderId: id,
            orderItemId: value?.orderItemId,
            code: value?.ticketCode || value?.verifyCode,
            vgsTicketId: value?.verifyCode || value?.ticketCode,
            lang: lang,
          };
          const searchParams = new URLSearchParams(objParams);
          return (
            <div style={{ textAlign: 'left' }}>
              <a
                href={`/deliver-view-pdf/${id}?${searchParams}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#F8B117',
                  textDecoration: 'underline',
                }}
                onClick={e => {
                  e.preventDefault();

                  if (
                    !!otpRef.current &&
                    accessDeliverPrintTicketFromEmailLink
                  ) {
                    localStorage.setItem(
                      DELIVER_PRINT_TICKETS_GUEST_FLOW_DATA,
                      JSON.stringify({
                        otp: otpRef.current,
                        guest: accessDeliverPrintTicketFromEmailLink ? '1' : '',
                      }),
                    );
                  }
                  setTimeout(() => {
                    window.open(
                      `/deliver-view-pdf/${id}?${searchParams}`,
                      '_blank',
                    );
                  }, 100);
                }}
              >
                {value?.productName + ' ' + Number(value?.index + 1)}
              </a>
            </div>
          );
        },
      },
      {
        key: 'action',
        render: value => {
          const objParams: Record<string, string> = {
            orderId: id,
            orderItemId: value?.orderItemId,
            code: value?.ticketCode || value?.verifyCode,
            vgsTicketId: value?.verifyCode || value?.ticketCode,
            lang: lang,
          };
          if (!!otpRef.current) {
            objParams.otp = otpRef.current;
          }
          if (accessDeliverPrintTicketFromEmailLink) {
            objParams.guest = '1';
          }
          const searchParams = new URLSearchParams(objParams);
          const name = value?.productName + ' ' + Number(value?.index + 1);
          return <DownLoadIcon searchParams={searchParams} name={name} />;
        },
      },
    ],
    [accessDeliverPrintTicketFromEmailLink, id, lang, maxFile, selectedParams],
  );

  const onSelectChangeCallback = value => {
    setSelected(value);
  };

  useEffect(() => {
    const isNeedOtp = rawError?.data?.errorType === 'errorOtp';
    if (isNeedOtp) setShowOtpModal(true);
    else setShowOtpModal(false);
  }, [rawError]);

  if (isLoading || isLoadingDetail) {
    return (
      <div style={styles}>
        <LoadingContainer loading>
          <div style={{ display: 'flex', flex: 1 }} />
        </LoadingContainer>
      </div>
    );
  }

  if (showOtpModal)
    return <OTPVerification onVerifyOtp={onVerifyOtp} loading={isVerifyOtp} />;
  const downloadAllPdf = () => {
    if (data?.items?.length > 0) {
      downloadPdfApi(
        id,
        setIsLoadingDownAll,
        moment().format('DDMMyyyyhhmmss'),
        otpRef.current,
        accessDeliverPrintTicketFromEmailLink ? '1' : undefined,
        lang,
      );
    }
  };
  return (
    <StyledStack style={styles}>
      <StyledStack
        style={{ background: 'white', flex: 1 }}
        padding={{ lg: '60px 30px', xs: '30px 15px' }}
      >
        <StyledStack
          direction={'column'}
          rowGap={'26px'}
          style={{ width: '100%' }}
        >
          <div
            className="d-flex align-items-center flex-wrap"
            style={{ columnGap: '36px', rowGap: '6px', width: '100%' }}
          >
            <StyledStack
              columnGap={'10px'}
              alignItem="center"
              style={{ width: 'initial' }}
            >
              <Tooltip
                color="white"
                title={translations?.['b2c.pdf.download-all']}
                overlayInnerStyle={{
                  color: 'black',
                }}
              >
                <StyledStack
                  style={{ width: 20, height: 20, marginRight: 'auto' }}
                  onClick={downloadAllPdf}
                >
                  {isLoadingDownAll ? (
                    <LoadingOutlined style={{ color: '#F8B117' }} spin />
                  ) : (
                    <DownloadIcon style={{ cursor: 'pointer' }} />
                  )}
                </StyledStack>
              </Tooltip>
              {(infoUser?.businessPartner?.allowViewAllPdf ||
                accessDeliverPrintTicketFromEmailLink) && (
                <StyledButton
                  textColor="#fff"
                  height="40px"
                  width="auto"
                  className={isMobile ? 'p-1' : 'ms-5'}
                  onClick={() =>
                    downloadAndViewPdf(
                      id,
                      setIsLoadingViewAll,
                      otpRef.current,
                      accessDeliverPrintTicketFromEmailLink ? '1' : undefined,
                      lang,
                    )
                  }
                  disabled={isLoadingViewAll}
                >
                  {translations?.['b2c.txt.viewAllPdf'] ?? ''}
                </StyledButton>
              )}
            </StyledStack>
          </div>
          <StyledStack direction={'column'} rowGap={'16px'}>
            <StyledStack width={{ lg: 'fit-content', xs: '100%' }}>
              {dataSources?.length ? (
                <StyledTable
                  dataSource={dataSources}
                  columns={columns}
                  onSelectChangeCallback={onSelectChangeCallback}
                  pagination={{
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    onChange: () => {
                      document.body.scrollTop = 0; // For Safari
                      document.documentElement.scrollTop = 0;
                    },
                  }}
                />
              ) : (
                <div>
                  {translations?.['b2b.txt.pdfProccessing'] ?? ''}{' '}
                  <DownloadIcon style={{ marginLeft: 6 }} />
                </div>
              )}
            </StyledStack>
          </StyledStack>
        </StyledStack>
      </StyledStack>
    </StyledStack>
  );
});

const styles: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  background: 'white',
  zIndex: 1,
  display: 'block',
  height: 'auto',
};
